
import { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import PriceHistoryChart from './components/PriceHistoryChart';
import RetailerComparison from './components/RetailerComparison';
import PriceAlertForm from './components/PriceAlertForm';
import ReviewsSection from './components/ReviewsSection';
import ShareModal from '../../components/feature/ShareModal';
import { productsData } from '../../mocks/products';
import { usePriceMonitor } from '../../hooks/usePriceMonitor';
import { useFavorites } from '../../hooks/useFavorites';
import { getAverageRating, getProductReviews } from '../../mocks/reviews';

const ProductPage = () => {
  const { id } = useParams<{ id: string }>();
  const [selectedImage, setSelectedImage] = useState(0);
  const [trackedAnimation, setTrackedAnimation] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const { addToTracking, isTracked } = usePriceMonitor();
  const { addToFavorites, removeFromFavorites, isFavorite } = useFavorites();

  const product = useMemo(() => {
    return productsData.find(p => p.id === Number(id));
  }, [id]);

  if (!product) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="max-w-7xl mx-auto px-4 py-20 text-center">
          <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <i className="ri-search-line text-4xl text-gray-400"></i>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Product Not Found</h1>
          <p className="text-gray-600 mb-8">The product you are looking for does not exist or has been removed.</p>
          <Link
            to="/shop"
            className="inline-flex items-center gap-2 px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i>
            Back to Shop
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const allImages = [product.image, ...product.gallery];
  const lowestRetailer = product.retailers.reduce((min, r) => r.price < min.price ? r : min);
  const tracked = isTracked(product.id);
  const favorited = isFavorite(product.id);
  const averageRating = getAverageRating(product.id);
  const reviews = getProductReviews(product.id);
  const savings = product.highestPrice - lowestRetailer.price;
  const discountPercent = Math.round((savings / product.highestPrice) * 100);

  const handleTrackPrice = () => {
    addToTracking({
      id: product.id,
      name: product.name,
      image: product.image,
      currentPrice: lowestRetailer.price,
      retailer: lowestRetailer.name,
    });
    setTrackedAnimation(true);
    setTimeout(() => setTrackedAnimation(false), 1000);
  };

  const handleFavoriteToggle = () => {
    if (favorited) {
      removeFromFavorites(product.id);
    } else {
      addToFavorites({
        id: product.id,
        name: product.name,
        price: lowestRetailer.price,
        image: product.image,
        category: product.category,
      });
    }
  };

  const renderStars = (rating: number) => {
    return (
      <div className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map(star => (
          <i
            key={star}
            className={`text-sm ${star <= Math.round(rating) ? 'ri-star-fill text-amber-400' : 'ri-star-line text-gray-300'}`}
          ></i>
        ))}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      {/* Breadcrumb */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <nav className="flex items-center gap-2 text-sm">
            <Link to="/" className="text-gray-500 hover:text-gray-700 cursor-pointer">Home</Link>
            <i className="ri-arrow-right-s-line text-gray-400"></i>
            <Link to="/shop" className="text-gray-500 hover:text-gray-700 cursor-pointer">Compare Prices</Link>
            <i className="ri-arrow-right-s-line text-gray-400"></i>
            <span className="text-gray-900 font-medium">{product.name}</span>
          </nav>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Product Overview */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 lg:p-8 mb-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Image Gallery */}
            <div>
              <div className="aspect-square bg-gray-50 rounded-xl overflow-hidden mb-4">
                <img
                  src={allImages[selectedImage]}
                  alt={product.name}
                  className="w-full h-full object-contain object-top"
                />
              </div>
              {allImages.length > 1 && (
                <div className="flex gap-3">
                  {allImages.map((img, index) => (
                    <button
                      key={index}
                      onClick={() => setSelectedImage(index)}
                      className={`w-20 h-20 rounded-lg overflow-hidden border-2 transition-all cursor-pointer ${
                        selectedImage === index ? 'border-teal-500' : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <img src={img} alt="" className="w-full h-full object-contain object-top" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Product Info */}
            <div>
              <div className="flex items-start justify-between mb-4">
                <div>
                  <p className="text-sm text-teal-600 font-medium mb-1">{product.brand}</p>
                  <h1 className="text-2xl lg:text-3xl font-bold text-gray-900">{product.name}</h1>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setShowShareModal(true)}
                    className="w-10 h-10 rounded-full flex items-center justify-center bg-gray-100 text-gray-500 hover:bg-teal-100 hover:text-teal-600 transition-all cursor-pointer"
                    title="Share"
                  >
                    <i className="ri-share-line text-xl"></i>
                  </button>
                  <button
                    onClick={handleFavoriteToggle}
                    className={`w-10 h-10 rounded-full flex items-center justify-center transition-all cursor-pointer ${
                      favorited ? 'bg-red-100 text-red-500' : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                    }`}
                  >
                    <i className={`${favorited ? 'ri-heart-fill' : 'ri-heart-line'} text-xl`}></i>
                  </button>
                </div>
              </div>

              {/* Rating Summary */}
              {averageRating > 0 && (
                <div className="flex items-center gap-3 mb-4">
                  {renderStars(averageRating)}
                  <span className="text-sm font-medium text-gray-900">{averageRating}</span>
                  <span className="text-sm text-gray-500">({reviews.length} reviews)</span>
                </div>
              )}

              <p className="text-gray-600 mb-6">{product.description}</p>

              {/* Price Display */}
              <div className="bg-gradient-to-r from-teal-50 to-emerald-50 rounded-xl p-6 mb-6">
                <div className="flex items-end gap-3 mb-2">
                  <span className="text-4xl font-bold text-gray-900">${lowestRetailer.price}</span>
                  <span className="text-lg text-gray-500 line-through">${product.highestPrice}</span>
                  <span className="px-2 py-1 bg-green-500 text-white text-sm rounded-full">
                    -{discountPercent}%
                  </span>
                </div>
                <p className="text-sm text-gray-600">
                  Best price at <strong>{lowestRetailer.name}</strong> • Save ${savings}
                </p>
              </div>

              {/* Price Range Bar */}
              <div className="mb-6">
                <div className="flex items-center justify-between text-sm text-gray-500 mb-2">
                  <span>Price Range (6 months)</span>
                  <span>${product.lowestPrice} - ${product.highestPrice}</span>
                </div>
                <div className="h-3 bg-gray-200 rounded-full relative">
                  <div
                    className="absolute h-full bg-gradient-to-r from-green-400 to-teal-500 rounded-full"
                    style={{
                      left: '0%',
                      width: `${((lowestRetailer.price - product.lowestPrice) / (product.highestPrice - product.lowestPrice)) * 100}%`
                    }}
                  />
                  <div
                    className="absolute w-4 h-4 bg-teal-600 rounded-full -top-0.5 border-2 border-white shadow-lg"
                    style={{
                      left: `${((lowestRetailer.price - product.lowestPrice) / (product.highestPrice - product.lowestPrice)) * 100}%`,
                      transform: 'translateX(-50%)'
                    }}
                  />
                </div>
                <div className="flex items-center justify-between text-xs text-gray-400 mt-1">
                  <span>Lowest</span>
                  <span>Current</span>
                  <span>Highest</span>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 mb-6">
                <a
                  href={lowestRetailer.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 py-3 bg-teal-600 text-white rounded-lg font-medium hover:bg-teal-700 transition-colors text-center cursor-pointer whitespace-nowrap"
                >
                  <span className="flex items-center justify-center gap-2">
                    Buy at {lowestRetailer.name}
                    <i className="ri-external-link-line"></i>
                  </span>
                </a>
                <button
                  onClick={handleTrackPrice}
                  disabled={tracked}
                  className={`px-6 py-3 rounded-lg font-medium transition-all cursor-pointer whitespace-nowrap ${
                    tracked
                      ? 'bg-teal-100 text-teal-700 border border-teal-200'
                      : trackedAnimation
                      ? 'bg-teal-600 text-white scale-95'
                      : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {tracked ? (
                    <span className="flex items-center gap-2">
                      <i className="ri-check-line"></i>
                      Tracking
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <i className="ri-line-chart-line"></i>
                      Track
                    </span>
                  )}
                </button>
                <button
                  onClick={() => setShowShareModal(true)}
                  className="px-6 py-3 rounded-lg font-medium border border-gray-300 text-gray-700 hover:bg-gray-50 transition-all cursor-pointer whitespace-nowrap"
                >
                  <span className="flex items-center gap-2">
                    <i className="ri-share-line"></i>
                    Share
                  </span>
                </button>
              </div>

              {/* Specs */}
              <div className="border-t pt-6">
                <h3 className="text-sm font-semibold text-gray-900 mb-4">Specifications</h3>
                <div className="grid grid-cols-2 gap-3">
                  {product.specs.map((spec, index) => (
                    <div key={index} className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg">
                      <span className="text-sm text-gray-500">{spec.label}</span>
                      <span className="text-sm font-medium text-gray-900">{spec.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Price History & Retailers */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
          <div className="lg:col-span-2">
            <PriceHistoryChart
              data={product.priceHistory}
              lowestPrice={product.lowestPrice}
              highestPrice={product.highestPrice}
              currentPrice={lowestRetailer.price}
            />
          </div>
          <div>
            <PriceAlertForm
              productId={product.id}
              productName={product.name}
              currentPrice={lowestRetailer.price}
              lowestPrice={product.lowestPrice}
            />
          </div>
        </div>

        {/* Retailer Comparison */}
        <RetailerComparison retailers={product.retailers} productName={product.name} />

        {/* Reviews Section */}
        <div className="mt-8">
          <ReviewsSection productId={product.id} />
        </div>

        {/* Related Products */}
        <div className="mt-12">
          <h2 className="text-xl font-bold text-gray-900 mb-6">Similar Products</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {productsData
              .filter(p => p.category === product.category && p.id !== product.id)
              .slice(0, 4)
              .map(relatedProduct => {
                const relatedLowest = relatedProduct.retailers.reduce((min, r) => r.price < min.price ? r : min);
                return (
                  <Link
                    key={relatedProduct.id}
                    to={`/product/${relatedProduct.id}`}
                    className="bg-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow cursor-pointer"
                  >
                    <div className="aspect-square bg-gray-50 p-4">
                      <img
                        src={relatedProduct.image}
                        alt={relatedProduct.name}
                        className="w-full h-full object-contain object-top"
                      />
                    </div>
                    <div className="p-4">
                      <h3 className="text-sm font-medium text-gray-900 mb-1 line-clamp-1">{relatedProduct.name}</h3>
                      <p className="text-lg font-bold text-teal-600">${relatedLowest.price}</p>
                    </div>
                  </Link>
                );
              })}
          </div>
        </div>
      </main>

      <Footer />

      {/* Share Modal */}
      <ShareModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        title={`${product.name} - ${discountPercent}% OFF!`}
        url={window.location.href}
        description={`Save $${savings} on ${product.name} at ${lowestRetailer.name}!`}
        image={product.image}
      />
    </div>
  );
};

export default ProductPage;
